import {
  fromUrl,
  type GeoTIFFImage,
  type GeotiffWriterMetadata,
  type ReadRasterResult,
} from "geotiff";
import proj4 from "proj4";

import type { JsHeapSnapshot } from "./jsHeapMemory";
import {
  createOrthoImageOverlay,
  VIEWPORT_TILE_BUFFER_RATIO,
  type ImageCornerCoordinates,
  type LngLatBoundsLike,
  type OrthoDetailTile,
  type OrthoImageOverlay,
} from "./orthoAssets";
import { rastersToPngBytesInWorker } from "./rastersToPngWorkerClient";

export type CogTiff = Awaited<ReturnType<typeof fromUrl>>;

/** Full-res COG windows served as Mapbox image sources. */
export const COG_DETAIL_TILE_SIZE_PX = 1024;

/** Cap parallel full-res window decodes — each 1024² RGBA tile is ~4MB raw. */
export const COG_DETAIL_TILE_CONCURRENCY = 3;

/**
 * Decoded Mapbox tile blob budget. At 1024px this ortho is ~572 windows;
 * keep the whole grid so zoom-out/in does not evict viewport windows.
 */
export const COG_DETAIL_TILE_CACHE_LIMIT = 600;

/** geotiff BlockedSource size; enables in-memory reuse of COG Range responses. */
export const COG_SOURCE_BLOCK_SIZE = 256 * 1024;
export const COG_SOURCE_BLOCK_CACHE_SIZE = 1024;

/** Cap overview decode edge; keep under common WebGL max texture sizes. */
export const DEFAULT_MAX_TEXTURE_DIMENSION = 4096;

/** Upper bound for geotiff.js Range-response block cache (blocks × blockSize). */
export const COG_SOURCE_BLOCK_CACHE_BUDGET_BYTES =
  COG_SOURCE_BLOCK_SIZE * COG_SOURCE_BLOCK_CACHE_SIZE;

export interface CogOrthoMemoryStats {
  detailCacheBlobBytes: number;
  detailCacheLimit: number;
  detailCachePeakBlobBytes: number;
  detailCacheSize: number;
  geotiffBlockCacheBudgetBytes: number;
  jsHeap: JsHeapSnapshot | null;
  overviewBlobBytes: number;
}

export interface CogGeoTransform {
  epsgCode: number;
  height: number;
  originX: number;
  originY: number;
  resolutionX: number;
  resolutionY: number;
  samplesPerPixel: number;
  width: number;
}

export interface CogFullResWindow {
  height: number;
  rasters: ArrayLike<number>;
  samplesPerPixel: number;
  width: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** COG operations exposed to map LOD. */
export interface CogOrthoApi {
  getCachedDetailTile: (tileId: string) => OrthoDetailTile | null;
  getDetailCacheSize: () => number;
  getMemoryStats: () => CogOrthoMemoryStats;
  getWriterGeoMetadata: (
    windowX0: number,
    windowY0: number,
    width: number,
    height: number,
  ) => GeotiffWriterMetadata;
  listVisibleDetailTileIds: (
    mapBounds: LngLatBoundsLike,
    bufferRatio?: number,
  ) => string[];
  loadDetailTilesById: (
    tileIds: string[],
    options: {
      concurrency?: number;
      onTile: (tile: OrthoDetailTile) => void;
      shouldContinue?: () => boolean;
    },
  ) => Promise<{ cacheHits: number; cacheMisses: number }>;
  open: () => Promise<void>;
  pinDetailTiles: (tileIds: Iterable<string>) => void;
  readFullResWindow: (
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ) => Promise<CogFullResWindow>;
  wgs84ToPixel: (lng: number, lat: number) => [number, number];
}

// Leaving this empty for now in case we want to add project definitions proj4 doesn't support OOTB.
const PROJ_DEFS: Record<number, string> = {};

export const ensureProjDef = (epsgCode: number): void => {
  if (
    epsgCode === 4326 ||
    (epsgCode >= 32601 && epsgCode <= 32660) ||
    (epsgCode >= 32701 && epsgCode <= 32760)
  ) {
    return;
  }

  const def = PROJ_DEFS[epsgCode];
  if (def == null) {
    throw new Error(`Unsupported GeoTIFF CRS EPSG:${epsgCode}`);
  }

  proj4.defs(`EPSG:${epsgCode}`, def);
};

export const getEpsgCode = (geoKeys: {
  GeographicTypeGeoKey?: number;
  ProjectedCSTypeGeoKey?: number;
}): number => {
  const projected = geoKeys.ProjectedCSTypeGeoKey;
  if (typeof projected === "number" && projected > 0) {
    return projected;
  }

  const geographic = geoKeys.GeographicTypeGeoKey;
  if (typeof geographic === "number" && geographic > 0) {
    return geographic;
  }

  throw new Error("GeoTIFF is missing a usable EPSG CRS geo key");
};

const toWgs84 = (epsgCode: number, x: number, y: number): [number, number] => {
  if (epsgCode === 4326) {
    return [x, y];
  }

  const [lng, lat] = proj4(`EPSG:${epsgCode}`, "EPSG:4326", [x, y]);
  return [lng, lat];
};

const fromWgs84 = (
  epsgCode: number,
  lng: number,
  lat: number,
): [number, number] => {
  if (epsgCode === 4326) {
    return [lng, lat];
  }

  const [x, y] = proj4("EPSG:4326", `EPSG:${epsgCode}`, [lng, lat]);
  return [x, y];
};

export const pixelToWgs84 = (
  transform: CogGeoTransform,
  pixelX: number,
  pixelY: number,
): [number, number] => {
  const crsX = transform.originX + pixelX * transform.resolutionX;
  const crsY = transform.originY + pixelY * transform.resolutionY;
  return toWgs84(transform.epsgCode, crsX, crsY);
};

export const wgs84ToPixelCoords = (
  transform: CogGeoTransform,
  lng: number,
  lat: number,
): [number, number] => {
  const [crsX, crsY] = fromWgs84(transform.epsgCode, lng, lat);
  const pixelX = (crsX - transform.originX) / transform.resolutionX;
  const pixelY = (crsY - transform.originY) / transform.resolutionY;
  return [pixelX, pixelY];
};

export const windowToCoordinates = (
  transform: CogGeoTransform,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): ImageCornerCoordinates => [
  pixelToWgs84(transform, x0, y0),
  pixelToWgs84(transform, x1, y0),
  pixelToWgs84(transform, x1, y1),
  pixelToWgs84(transform, x0, y1),
];

export const getTileLngLatBounds = (coordinates: ImageCornerCoordinates) => {
  const lngs = coordinates.map(([lng]) => lng);
  const lats = coordinates.map(([, lat]) => lat);
  return {
    east: Math.max(...lngs),
    north: Math.max(...lats),
    south: Math.min(...lats),
    west: Math.min(...lngs),
  };
};

export const toInterleavedRasters = (
  rasters: ReadRasterResult,
): ArrayLike<number> => {
  if (Array.isArray(rasters)) {
    throw new Error("Expected interleaved GeoTIFF rasters");
  }
  return rasters;
};

export const rastersToObjectUrl = async (
  rasters: ReadRasterResult,
  width: number,
  height: number,
  samplesPerPixel: number,
): Promise<{ blob: Blob; byteLength: number; imageUrl: string }> => {
  const pngBytes = await rastersToPngBytesInWorker(
    toInterleavedRasters(rasters),
    width,
    height,
    samplesPerPixel,
  );
  const blob = new Blob([pngBytes], { type: "image/png" });
  return {
    blob,
    byteLength: blob.size,
    imageUrl: URL.createObjectURL(blob),
  };
};

export const buildOverviewOverlay = async (
  tiff: CogTiff,
  transform: CogGeoTransform,
  maxTextureDimension: number = DEFAULT_MAX_TEXTURE_DIMENSION,
): Promise<{
  blob: Blob;
  byteLength: number;
  height: number;
  overlay: OrthoImageOverlay;
  width: number;
}> => {
  const longestEdge = Math.max(transform.width, transform.height);
  const scale = Math.min(1, maxTextureDimension / longestEdge);
  const outputWidth = Math.max(1, Math.round(transform.width * scale));
  const outputHeight = Math.max(1, Math.round(transform.height * scale));

  const rasters = await tiff.readRasters({
    height: outputHeight,
    interleave: true,
    resampleMethod: "bilinear",
    width: outputWidth,
  });

  const { blob, byteLength, imageUrl } = await rastersToObjectUrl(
    rasters,
    outputWidth,
    outputHeight,
    transform.samplesPerPixel,
  );
  const coordinates = windowToCoordinates(
    transform,
    0,
    0,
    transform.width,
    transform.height,
  );

  return {
    blob,
    byteLength,
    height: outputHeight,
    overlay: createOrthoImageOverlay(coordinates, imageUrl),
    width: outputWidth,
  };
};

/**
 * Ground sample distance (cm/px) from the COG's native pixel size. Only
 * meaningful for projected CRSs, which we support only in meters (UTM); returns
 * null for geographic CRSs (pixel size is in degrees) or when geo keys are absent.
 */
export const getCogCmPerPx = (image: GeoTIFFImage): number | null => {
  const projected: unknown = image.getGeoKeys()?.ProjectedCSTypeGeoKey;
  if (typeof projected !== "number" || projected <= 0) {
    return null;
  }

  const [resolutionX] = image.getResolution();
  return Number.isFinite(resolutionX) ? Math.abs(resolutionX) * 100 : null;
};

/** Small enough to decode quickly for a folder-row icon; not meant for map display. */
export const DEFAULT_THUMBNAIL_MAX_DIMENSION_PX = 64;

/** Small non-georeferenced preview image, decoded straight from a COG URL (no session/transform needed). */
export const buildThumbnailImageUrl = async (
  url: string,
  maxDimensionPx: number = DEFAULT_THUMBNAIL_MAX_DIMENSION_PX,
): Promise<string> => {
  const tiff = await fromUrl(url);
  const image = await tiff.getImage();
  const longestEdge = Math.max(image.getWidth(), image.getHeight());
  const scale = Math.min(1, maxDimensionPx / longestEdge);
  const width = Math.max(1, Math.round(image.getWidth() * scale));
  const height = Math.max(1, Math.round(image.getHeight() * scale));

  const rasters = await tiff.readRasters({
    height,
    interleave: true,
    resampleMethod: "bilinear",
    width,
  });

  const { imageUrl } = await rastersToObjectUrl(
    rasters,
    width,
    height,
    image.getSamplesPerPixel(),
  );
  return imageUrl;
};

/**
 * Cancellation-safe thumbnail load: decodes `url` and calls `onLoaded` with the
 * resulting blob URL unless the returned cleanup already ran. Revokes the blob URL
 * on cleanup (whether or not it arrived before cancellation).
 */
export const loadThumbnailImageUrl = (
  url: string,
  onLoaded: (imageUrl: string) => void,
  maxDimensionPx: number = DEFAULT_THUMBNAIL_MAX_DIMENSION_PX,
): (() => void) => {
  let cancelled = false;
  let objectUrl: string | null = null;

  buildThumbnailImageUrl(url, maxDimensionPx).then(
    (imageUrl) => {
      if (cancelled) {
        URL.revokeObjectURL(imageUrl);
        return;
      }
      objectUrl = imageUrl;
      onLoaded(imageUrl);
    },
    (error: unknown) => {
      console.error("[CogOrtho] Failed to build thumbnail:", error);
    },
  );

  return () => {
    cancelled = true;
    if (objectUrl != null) {
      URL.revokeObjectURL(objectUrl);
    }
  };
};

export const buildWriterGeoMetadata = (
  image: GeoTIFFImage,
  transform: CogGeoTransform,
  windowX0: number,
  windowY0: number,
  width: number,
  height: number,
): GeotiffWriterMetadata => {
  const bitsPerSample = image.getBitsPerSample(0);
  const sampleFormat = image.getSampleFormat(0);
  const tiepointX = transform.originX + windowX0 * transform.resolutionX;
  const tiepointY = transform.originY + windowY0 * transform.resolutionY;
  const isProjected = transform.epsgCode !== 4326;

  return {
    BitsPerSample: [bitsPerSample, bitsPerSample, bitsPerSample, bitsPerSample],
    Compression: 1,
    ExtraSamples: [2],
    GTModelTypeGeoKey: isProjected ? 1 : 2,
    GTRasterTypeGeoKey: 1,
    ImageLength: height,
    ImageWidth: width,
    ModelPixelScale: [
      Math.abs(transform.resolutionX),
      Math.abs(transform.resolutionY),
      0,
    ],
    ModelTiepoint: [0, 0, 0, tiepointX, tiepointY, 0],
    PhotometricInterpretation: 2,
    PlanarConfiguration: 1,
    SampleFormat: [sampleFormat, sampleFormat, sampleFormat, sampleFormat],
    SamplesPerPixel: 4,
    height,
    width,
    ...(isProjected
      ? { ProjectedCSTypeGeoKey: transform.epsgCode }
      : { GeographicTypeGeoKey: transform.epsgCode }),
  };
};

export { VIEWPORT_TILE_BUFFER_RATIO };
